
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Bell, Shield, CreditCard, Globe, Lock } from "lucide-react";
import toast from "react-hot-toast";

export default function AdminSettingsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const handleSave = () => {
    toast.success("Settings saved successfully");
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold font-playfair mb-0.5">Settings</h1>
        <p className="text-xs text-gray-500">Manage your admin panel settings</p>
      </div>

      <div className="space-y-4">
        {/* General Settings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <Globe className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">General Settings</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Site Name
              </label>
              <input
                type="text"
                defaultValue="Aarsa"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Site Email
              </label>
              <input
                type="email"
                defaultValue="admin@aarsa.com"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all bg-white"
              />
            </div>
          </div>
        </motion.div>

        {/* Payment Settings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Payment Settings</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Razorpay Key ID
              </label>
              <input
                type="text"
                placeholder="Enter Razorpay Key ID"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Razorpay Key Secret
              </label>
              <div className="relative">
                <input
                  type="password"
                  placeholder="Enter Razorpay Key Secret"
                  className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all bg-white"
                />
                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Notification Settings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Notification Settings</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition-colors -mx-2">
              <input
                type="checkbox"
                defaultChecked
                className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">Email notifications for new orders</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition-colors -mx-2">
              <input
                type="checkbox"
                defaultChecked
                className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">Low stock alerts</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group hover:bg-gray-50 p-2 rounded-lg transition-colors -mx-2">
              <input
                type="checkbox"
                className="w-4 h-4 text-aarsa-sage-600 rounded border-gray-300 focus:ring-2 focus:ring-aarsa-sage-500 focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">Daily sales reports</span>
            </label>
          </div>
        </motion.div>

        {/* Security Settings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Security Settings</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Change Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all bg-white"
                />
                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all bg-white"
                />
                <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end pt-2"
        >
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md"
          >
            <Save className="w-4 h-4" />
            Save Settings
          </button>
        </motion.div>
      </div>
    </div>
  );
}
